import {
  mediaQueryLarge,
  requestIdleCallback,
  startViewTransition,
} from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated results list.
 */
export default class ResultsList extends PaginatedList {
  /** @type {boolean} */
  #isConnected = false;

  /**
   * Identifies the latest requested layout change.
   * Older transitions and storage callbacks are ignored.
   *
   * @type {number}
   */
  #layoutChangeVersion = 0;

  connectedCallback() {
    if (this.#isConnected) return;

    this.#isConnected = true;

    super.connectedCallback();

    mediaQueryLarge.addEventListener(
      'change',
      this.#handleMediaQueryChange
    );

    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    if (!this.#isConnected) return;

    this.#isConnected = false;
    this.#layoutChangeVersion += 1;

    mediaQueryLarge.removeEventListener(
      'change',
      this.#handleMediaQueryChange
    );

    this.removeAttribute('initialized');

    /*
     * Required so PaginatedList can disconnect its intersection observer,
     * document event listeners, mutation observer and cached page data.
     */
    super.disconnectedCallback();
  }

  /**
   * Updates the product-grid layout.
   *
   * @param {Event} event
   */
  updateLayout(event) {
    const { target } = event;

    if (!(target instanceof HTMLInputElement)) return;

    /*
     * Ignore an unchecked radio input because another input in the same
     * group will provide the selected value.
     */
    if (target.type === 'radio' && !target.checked) return;

    const value = target.value.trim();

    if (!value) return;

    void this.#animateLayoutChange(value);
  }

  /**
   * Animates a product-grid layout change.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!this.#isConnected || !grid) return;

    const version = ++this.#layoutChangeVersion;

    /*
     * Avoid creating another view transition when the selected layout is
     * already active.
     */
    if (grid.getAttribute('product-grid-view') === value) {
      this.#saveLayoutPreference(value, version);
      return;
    }

    try {
      await startViewTransition(
        () => {
          if (!this.#isConnected) return;
          if (version !== this.#layoutChangeVersion) return;

          this.#setLayout(value);
        },
        ['product-grid']
      );
    } catch (error) {
      /*
       * Fall back to an immediate layout update if a view transition fails
       * or is unavailable.
       */
      if (
        this.#isConnected &&
        version === this.#layoutChangeVersion
      ) {
        this.#setLayout(value);
      }
    }

    if (!this.#isConnected) return;
    if (version !== this.#layoutChangeVersion) return;

    this.#saveLayoutPreference(value, version);
  };

  /**
   * Saves the selected layout without blocking the current interaction.
   *
   * @param {string} value
   * @param {number} version
   */
  #saveLayoutPreference(value, version) {
    requestIdleCallback(() => {
      if (!this.#isConnected) return;
      if (version !== this.#layoutChangeVersion) return;

      const viewport = mediaQueryLarge.matches
        ? 'desktop'
        : 'mobile';

      try {
        sessionStorage.setItem(
          `product-grid-view-${viewport}`,
          value
        );
      } catch (error) {
        /*
         * sessionStorage can be unavailable in restricted browsing modes.
         * The layout still works without saving the preference.
         */
      }
    });
  }

  /**
   * Applies a product-grid layout value.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;

    if (!grid || !value) return;

    if (grid.getAttribute('product-grid-view') === value) return;

    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Resets the grid to the correct default when moving between desktop
   * and mobile viewports.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    if (!this.#isConnected) return;

    /*
     * Invalidate any layout transition that began for the previous
     * viewport size.
     */
    this.#layoutChangeVersion += 1;

    const targetElement = event.matches
      ? this.querySelector(
          '[data-grid-layout="desktop-default-option"]'
        )
      : this.querySelector(
          '[data-grid-layout="mobile-option"]'
        );

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}